import { ShipmentBookingType } from "../Shipments";
import { ShipmentBox } from "./ShipmentBoxes";

export interface ShipmentProductApiRow {
  product_id: number;
  swagpack_id: number | null;
  pack_label?: string | null;
  product_name: string;
  sku: string | null;
  quantity: number;
  line_id?: number | null;
}

export interface ShipmentDetailsApiRow {
  id: number;
  customer_id: number | null;
  customer_name: string | null;
  inventory_id: number | null;
  store_id: number | null;
  coupon_id: string | null;
  campaign_id: number | null;
  shipment_type: number | null;
  shipment_type_label: string | null;
  status: number | null;
  status_label: string | null;
  first_name: string;
  last_name: string | null;
  email: string;
  calling_code: string | null;
  phone: string;
  alternate_phone?: string | null;
  address1: string;
  address2: string | null;
  address3: string | null;
  landmark: string | null;
  city: string;
  state: string;
  country: string;
  pincode: string;
  awb: string | null;
  awb_track_url: string | null;
  additional_awb: string[];
  /** Header dims are omitted on get-by-id; booking uses the first active box. */
  length?: number | null;
  breadth?: number | null;
  height?: number | null;
  weight?: number | null;
  boxes?: ShipmentBox[];
  shipping_cost: number | null;
  delivery_partner_id: number | null;
  delivery_partner_name: string | null;
  shipment_method: string | null;
  booking_type?: ShipmentBookingType | string | null;
  active?: boolean | number | null;
  booking_state?: string | null;
  booking_attempts?: number | null;
  last_booked_at?: Date | string | null;
  last_booking_error?: string | null;
  carrier_ref_no?: string | null;
  deliver_partner_tracking_url: string | null;
  paid: boolean | null;
  placed_by: number | null;
  placed_by_user: string | null;
  placed_by_admin_id: number | null;
  placed_by_admin_name: string | null;
  shipped_status_date: Date | string | null;
  delivered_status_date: Date | string | null;
  status_change_date: Date | string | null;
  created_date: Date | string | null;
  modified_date: Date | string | null;
  order_notes: string | null;
  expected_delivery_date?: Date | string | null;
  dispatch_date?: Date | string | null;
  declared_value?: number | null;
  label_s3_key?: string | null;
  mps_label_s3_key?: string | null;
  products: ShipmentProductApiRow[];
  is_sample_sir_shipment?: boolean;
  stock_issue_request_id?: number | null;
  lines_removable?: boolean;
}

export type ShipmentSectionErrorCode =
  | "SHIPMENT_NOT_FOUND"
  | "RECIPIENT_NOT_EDITABLE"
  | "ADDRESS_NOT_EDITABLE"
  | "DELIVERY_NOT_EDITABLE";

export interface UpdateShipmentRecipientRequest {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  alternate_phone: string;
}

export interface UpdateShipmentRecipientResult {
  shipmentId: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  alternate_phone: string;
}

export interface UpdateShipmentAddressRequest {
  address1: string;
  address2: string;
  landmark: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  order_notes: string;
}

export interface UpdateShipmentAddressResult {
  shipmentId: number;
  address1: string;
  address2: string;
  landmark: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  order_notes: string;
}

export interface UpdateShipmentDeliveryRequest {
  status: number;
  expected_delivery_date?: string | null;
  dispatch_date?: string | null;
  delivery_partner_id?: number;
  shipment_method?: string;
  booking_type?: ShipmentBookingType | string | null;
  shipping_cost?: number | null;
  awb?: string | null;
  awb_track_url?: string | null;
  declared_value?: number | null;
}

export interface UpdateShipmentDeliveryResult {
  shipmentId: number;
  expected_delivery_date: string | null;
  delivery_partner_id: number | null;
  shipment_method: string | null;
  booking_type?: ShipmentBookingType | string | null;
  status: number;
  dispatch_date: string | null;
  shipping_cost: number | null;
  awb: string | null;
  awb_track_url: string | null;
  declared_value?: number | null;
}

export function getShipmentSectionApiErrorMessage(
  error: unknown,
  fallback = "Failed to save shipment section.",
): string {
  try {
    const data = (
      error as {
        response?: {
          data?: {
            message?: string;
            code?: ShipmentSectionErrorCode | string;
          };
        };
        message?: string;
      }
    )?.response?.data;

    return (
      data?.message || (error as { message?: string })?.message || fallback
    );
  } catch {
    return fallback;
  }
}
